import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  GitCompare,
  Check,
  Cpu,
  Search,
  MousePointerClick,
  Database,
  Cable,
  CircuitBoard,
  ArrowRight,
  SlidersHorizontal,
} from 'lucide-react';
import { boardsAPI, icPinoutsAPI } from '../services/api';
import { formatBoardVoltage } from '../utils/compatibilityDisplay';
import { buildBoardSvgPreviewSrc } from '../utils/boardSvgPreview';
import { buildComparePath, resolveCompareBoardSelection } from '../utils/compareUrls';

const QUICK_FILTERS = [
  { key: 'arduino', label: 'Arduino', searchTerm: 'arduino' },
  { key: 'esp32', label: 'ESP32', searchTerm: 'esp32' },
  { key: 'raspberry-pi', label: 'Raspberry Pi', searchTerm: 'raspberry pi' },
  { key: 'sensor', label: 'Sensors', filterKey: 'sensor' },
  { key: 'logic-ic', label: 'Logic ICs', filterKey: 'logic-ic' },
  { key: 'modules', label: 'Modules', searchTerm: 'module' },
];

const BENEFIT_CARDS = [
  {
    title: 'Interactive diagrams',
    description: 'Click pins to view functions, voltage, and capabilities.',
    icon: MousePointerClick,
  },
  {
    title: 'Board specs in one place',
    description: 'Flash, RAM, clock, voltage, and pin count.',
    icon: Database,
  },
  {
    title: 'Wiring-friendly data',
    description: 'Useful for GPIO selection and project planning.',
    icon: Cable,
  },
];

const POPULAR_BOARDS_LIMIT = 4;

const HomeContainer = styled.div`
  position: relative;
  padding: clamp(28px, 4vw, 44px) 0 56px;
  background:
    radial-gradient(circle at top left, rgba(44, 159, 164, 0.1), transparent 34%),
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.08), transparent 28%),
    linear-gradient(180deg, #f5f9fc 0%, #f8fbfd 42%, #f3f7fb 100%);
  isolation: isolate;
`;

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(28px, 5vw, 48px);
  margin-bottom: 28px;
  border: 1px solid rgba(199, 213, 224, 0.9);
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(244, 250, 252, 0.98) 100%);
  box-shadow: 0 32px 70px -58px rgba(15, 23, 42, 0.55);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(44, 159, 164, 0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(44, 159, 164, 0.06) 1px, transparent 1px);
    background-size: 30px 30px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.22), transparent 80%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto -8% -36% auto;
    width: min(420px, 48vw);
    aspect-ratio: 1;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(44, 159, 164, 0.14), transparent 68%);
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
`;

const HeroTitle = styled.h1`
  margin: 0 0 18px;
  color: #0f172a;
  font-size: clamp(2.35rem, 5vw, 3.7rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
  font-weight: 800;
`;

const HeroSubtitle = styled.p`
  max-width: 720px;
  margin: 0 auto 30px;
  color: #4f6378;
  font-size: clamp(1rem, 1.8vw, 1.18rem);
  line-height: 1.75;
`;

const SearchSection = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const SearchField = styled.div`
  position: relative;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #688196;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  min-height: 64px;
  padding: 18px 22px 18px 56px;
  border: 1.5px solid #c7d4e1;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  font-size: 16px;
  box-shadow: 0 28px 42px -34px rgba(15, 23, 42, 0.45);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow:
      0 0 0 4px rgba(44, 159, 164, 0.14),
      0 30px 42px -32px rgba(44, 159, 164, 0.42);
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #8aa0b2;
  }
`;

const HeroMetaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const ResultsCount = styled.p`
  margin: 0;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(199, 213, 224, 0.9);
  background: rgba(255, 255, 255, 0.78);
  color: #54687b;
  font-size: 0.9rem;
  font-weight: 500;
`;

const QuickFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const QuickFilterChip = styled.button`
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? '#2c9fa4' : '#d6e2ec')};
  background: ${props => (props.$active ? 'rgba(44, 159, 164, 0.14)' : 'rgba(255, 255, 255, 0.88)')};
  color: ${props => (props.$active ? '#0f6b70' : '#496074')};
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #2c9fa4;
    color: #0f6b70;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }
`;

const BenefitsStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.article`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 14px;
  align-items: start;
  padding: 18px 18px 17px;
  border-radius: 20px;
  border: 1px solid #d9e4ed;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 22px 36px -34px rgba(15, 23, 42, 0.38);
`;

const BenefitIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(44, 159, 164, 0.14);
  background: linear-gradient(180deg, rgba(44, 159, 164, 0.12) 0%, rgba(56, 189, 248, 0.08) 100%);
  color: #15747a;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BenefitTitle = styled.h3`
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 700;
`;

const BenefitText = styled.p`
  margin: 0;
  color: #5a7083;
  font-size: 0.95rem;
  line-height: 1.65;
`;

const BoardsViewLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 30px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const BoardsMain = styled.div`
  min-width: 0;
`;

const SectionBlock = styled.section`
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionTitleGroup = styled.div`
  max-width: 720px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  color: #0f172a;
  font-size: clamp(1.55rem, 2vw, 1.95rem);
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const SectionDescription = styled.p`
  margin: 0;
  color: #607488;
  line-height: 1.7;
`;

const SectionMeta = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #d8e4ee;
  background: rgba(255, 255, 255, 0.82);
  color: #567082;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
`;

const FeaturedBoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 18px;
`;

const BoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 22px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FiltersPanel = styled.aside`
  position: sticky;
  top: calc(var(--header-height) + 24px);
  padding: 22px 20px;
  border: 1px solid #d8e4ed;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 26px 40px -34px rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(8px);

  @media (max-width: 980px) {
    position: static;
    order: -1;
  }
`;

const FiltersHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 18px;
`;

const FiltersIconWrap = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 14px;
  background: rgba(44, 159, 164, 0.12);
  color: #15747a;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FiltersTitle = styled.h3`
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 1.08rem;
  font-weight: 800;
`;

const FiltersText = styled.p`
  margin: 0;
  color: #607488;
  font-size: 0.94rem;
  line-height: 1.65;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FilterButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid ${props => (props.$active ? '#2c9fa4' : '#d9e4ed')};
  background: ${props => (props.$active ? 'linear-gradient(180deg, #2c9fa4 0%, #237b80 100%)' : 'white')};
  color: ${props => (props.$active ? 'white' : '#4c6477')};
  font-size: 0.94rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  box-shadow: ${props => (props.$active ? '0 16px 28px -22px rgba(28, 101, 104, 0.9)' : 'none')};
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: #2c9fa4;
    background: ${props => (props.$active ? 'linear-gradient(180deg, #2c9fa4 0%, #237b80 100%)' : '#f8fbfd')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }
`;

const FilterButtonLabel = styled.span`
  min-width: 0;
`;

const FilterButtonCount = styled.span`
  flex-shrink: 0;
  min-width: 34px;
  padding: 4px 9px;
  border-radius: 999px;
  background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.16)' : '#ecf7f8')};
  color: ${props => (props.$active ? 'white' : '#176f74')};
  font-size: 0.76rem;
  font-weight: 800;
  text-align: center;
`;

const BoardCard = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${props => (props.$compact ? '24px' : '28px')};
  border-radius: 24px;
  border: 1px solid #d8e4ee;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 251, 255, 0.98) 100%);
  box-shadow: 0 24px 40px -34px rgba(15, 23, 42, 0.42);
  overflow: hidden;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 4px;
    background: linear-gradient(90deg, rgba(44, 159, 164, 0.7), rgba(56, 189, 248, 0.1));
    opacity: 0.55;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(44, 159, 164, 0.55);
    box-shadow: 0 34px 48px -34px rgba(15, 23, 42, 0.42);
  }
`;

const SelectionButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
  border: 1px solid ${props => (props.$checked ? '#2c9fa4' : '#d4e0ea')};
  background: ${props => (props.$checked ? '#2c9fa4' : 'rgba(255, 255, 255, 0.96)')};
  color: ${props => (props.$checked ? 'white' : '#577084')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  z-index: 1;

  &:hover {
    transform: translateY(-1px);
    border-color: #2c9fa4;
    color: ${props => (props.$checked ? 'white' : '#15747a')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }

  span,
  svg {
    width: 16px;
    height: 16px;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
  }
`;

const CardTop = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
  padding-right: ${props => (props.$hasSelection ? '42px' : '0')};
`;

const CardVisual = styled.div`
  width: ${props => (props.$compact ? '76px' : '88px')};
  height: ${props => (props.$compact ? '76px' : '88px')};
  flex-shrink: 0;
  border-radius: 22px;
  border: 1px solid #d6e3ee;
  background:
    radial-gradient(circle at 30% 22%, rgba(44, 159, 164, 0.16), transparent 48%),
    linear-gradient(180deg, #ffffff 0%, #eef6fb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
`;

const CardVisualImage = styled.img`
  width: 100%;
  height: 100%;
  padding: 10px;
  display: block;
  object-fit: contain;
`;

const CardVisualFallback = styled.div`
  color: #1a7d83;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 34px;
    height: 34px;
  }
`;

const CardTitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: ${props => (props.$compact ? '1.25rem' : '1.42rem')};
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const CardMetaText = styled.p`
  margin: 8px 0 0;
  color: #5c7285;
  font-size: 0.94rem;
  line-height: 1.5;
`;

const CardBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const CardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 11px;
  border-radius: 999px;
  background: #edf9fa;
  color: #176f74;
  font-size: 0.77rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

const CardDescription = styled.p`
  margin: 0 0 18px;
  color: #556b7f;
  line-height: 1.72;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${props => (props.$compact ? 2 : 3)};
`;

const CardSpecs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const CardSpec = styled.div`
  min-height: 72px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid #e1eaf2;
  background: rgba(255, 255, 255, 0.9);
`;

const CardSpecLabel = styled.span`
  display: block;
  margin-bottom: 4px;
  color: #6b8194;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardSpecValue = styled.span`
  display: block;
  color: #1f3447;
  font-size: 0.93rem;
  line-height: 1.45;
  font-weight: 700;
  word-break: break-word;
`;

const CardFooter = styled.div`
  margin-top: auto;
`;

const CardAction = styled(Link)`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 16px;
  border-radius: 15px;
  background: linear-gradient(180deg, #2c9fa4 0%, #237b80 100%);
  color: white;
  text-decoration: none;
  font-weight: 700;
  box-shadow: 0 18px 28px -22px rgba(28, 101, 104, 0.85);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(0.98);
    box-shadow: 0 20px 30px -20px rgba(28, 101, 104, 0.9);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 4px rgba(44, 159, 164, 0.16),
      0 20px 30px -20px rgba(28, 101, 104, 0.9);
  }
`;

const EmptyState = styled.div`
  padding: 44px 24px;
  border: 1px solid #d9e4ed;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.92);
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 10px;
  color: #425669;
  font-size: 1.18rem;
`;

const EmptyStateText = styled.p`
  margin: 0;
  color: #7b91a3;
  line-height: 1.7;
`;

const LoadingSpinner = styled.div`
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  border-radius: 24px;
  border: 1px solid #fecaca;
  background: rgba(254, 242, 242, 0.92);
  color: #b91c1c;
  font-size: 1rem;
  font-weight: 600;
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 34px;
  padding: 8px 0 0;
`;

const PaginationButton = styled.button`
  min-width: 42px;
  padding: 9px 13px;
  border: 1px solid ${props => (props.$active ? '#2c9fa4' : '#d8e4ee')};
  border-radius: 12px;
  background: ${props => (props.$active ? '#2c9fa4' : 'white')};
  color: ${props => (props.$active ? 'white' : '#5d7285')};
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: #2c9fa4;
    background: ${props => (props.$active ? '#2c9fa4' : '#f8fbfd')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  margin-left: 8px;
  color: #607488;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ComparisonBar = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 320px;
  padding: 14px 18px;
  border-radius: 18px;
  border: 1px solid rgba(216, 228, 238, 0.92);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 28px 42px -30px rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    left: 16px;
    right: 16px;
    transform: none;
    min-width: 0;
    flex-wrap: wrap;
  }
`;

const ComparisonCount = styled.span`
  color: #1f3447;
  font-weight: 700;
`;

const CompareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  background: #2c9fa4;
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: #1c6568;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }

  &:disabled {
    background: #9aa9b7;
    cursor: not-allowed;
  }
`;

const ClearSelectionButton = styled.button`
  padding: 10px 12px;
  border: 1px solid #d8e4ee;
  border-radius: 12px;
  background: white;
  color: #607488;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: #c4d3df;
    background: #f8fbfd;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.18);
  }
`;

const Copyright = styled.footer`
  margin-top: 54px;
  padding-top: 24px;
  border-top: 1px solid #dce7ef;
  text-align: center;
  color: #6a7f92;
  font-size: 0.92rem;
  line-height: 1.7;
`;

const stripHtmlAndTruncate = (html, maxLength = 150) => {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  const cleanText = textContent.replace(/\s+/g, ' ').trim();

  return cleanText.length > maxLength
    ? `${cleanText.substring(0, maxLength).trim()}...`
    : cleanText;
};

const getItemMeta = (item) => {
  const metaParts = [item.manufacturer, item.category].filter(Boolean);

  if (metaParts.length > 0) {
    return metaParts.join(' • ');
  }

  return item.itemType === 'ic'
    ? 'Integrated circuit reference'
    : 'Interactive board reference';
};

const getItemSpecs = (item) => {
  const pinsValue = item.pin_count ? `${item.pin_count} pins` : 'Not specified';

  if (item.itemType === 'ic') {
    return [
      { label: 'Pins', value: pinsValue },
      { label: 'Package', value: item.package_variant || item.package_type || 'Not specified' },
      { label: 'Category', value: item.category || 'Not specified' },
      { label: 'Maker', value: item.manufacturer || 'Not specified' },
    ];
  }

  return [
    { label: 'Pins', value: pinsValue },
    { label: 'Voltage', value: formatBoardVoltage(item) || 'Not specified' },
    { label: 'Clock', value: item.clock_speed || 'Not specified' },
    { label: 'Flash', value: item.flash_memory || 'Not specified' },
    { label: 'RAM', value: item.ram || 'Not specified' },
  ];
};

const CatalogCardVisual = ({ item, compact = false }) => {
  const [hasError, setHasError] = useState(false);
  const fallbackIcon = item.itemType === 'ic' ? <CircuitBoard /> : <Cpu />;

  if (item.itemType === 'board' && item.cardSvgSrc && !hasError) {
    return (
      <CardVisual aria-hidden="true" $compact={compact}>
        <CardVisualImage
          src={item.cardSvgSrc}
          alt=""
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onError={() => setHasError(true)}
        />
      </CardVisual>
    );
  }

  return (
    <CardVisual aria-hidden="true" $compact={compact}>
      <CardVisualFallback>{fallbackIcon}</CardVisualFallback>
    </CardVisual>
  );
};

const CatalogCard = ({
  item,
  index,
  compact = false,
  showSelection = false,
  isSelected = false,
  onToggleSelection,
}) => {
  const specs = getItemSpecs(item);
  const description = item.displayDescription
    ? stripHtmlAndTruncate(item.displayDescription, compact ? 120 : 150)
    : '';
  const detailLabel = item.itemType === 'ic' ? 'View IC Pinout' : 'View Pinout';

  return (
    <BoardCard
      $compact={compact}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.24) }}
    >
      {showSelection && item.itemType === 'board' && (
        <SelectionButton
          type="button"
          $checked={isSelected}
          onClick={() => onToggleSelection(item.id)}
          aria-pressed={isSelected}
          aria-label={isSelected ? `Remove ${item.displayName} from comparison` : `Add ${item.displayName} to comparison`}
        >
          {isSelected ? <Check size={16} /> : <span>+</span>}
        </SelectionButton>
      )}

      <CardTop $hasSelection={showSelection && item.itemType === 'board'}>
        <CatalogCardVisual item={item} compact={compact} />

        <CardTitleBlock>
          <CardTitle $compact={compact}>{item.displayName}</CardTitle>
          <CardMetaText>{getItemMeta(item)}</CardMetaText>
          <CardBadgeRow>
            <CardBadge>{item.itemType === 'ic' ? 'IC' : item.displayBadge}</CardBadge>
          </CardBadgeRow>
        </CardTitleBlock>
      </CardTop>

      {description && <CardDescription $compact={compact}>{description}</CardDescription>}

      <CardSpecs>
        {specs.map((spec) => (
          <CardSpec key={`${item.itemType}-${item.id}-${spec.label}`}>
            <CardSpecLabel>{spec.label}</CardSpecLabel>
            <CardSpecValue>{spec.value}</CardSpecValue>
          </CardSpec>
        ))}
      </CardSpecs>

      <CardFooter>
        <CardAction
          to={item.detailUrl}
          aria-label={`${detailLabel} for ${item.displayName}`}
        >
          {detailLabel}
          <ArrowRight size={16} />
        </CardAction>
      </CardFooter>
    </BoardCard>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [icPinouts, setIcPinouts] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBoards, setTotalBoards] = useState(0);
  const [pageSize] = useState(12);

  const [boardsCache, setBoardsCache] = useState(new Map());
  const [lastFetchTime, setLastFetchTime] = useState(0);

  const [selectedBoards, setSelectedBoards] = useState(new Set());

  const fetchBoards = useCallback(async () => {
    const now = Date.now();

    if (boardsCache.has('all-items') && (now - lastFetchTime) < 60000) {
      const cachedData = boardsCache.get('all-items');
      setBoards(cachedData.boards || []);
      setIcPinouts(cachedData.icPinouts || []);
      setTotalBoards((cachedData.boards || []).length + (cachedData.icPinouts || []).length);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [boardsResponse, icResponse] = await Promise.all([
        boardsAPI.getAll({ include_svg: 0 }),
        icPinoutsAPI.getAll(),
      ]);

      const boardsData = Array.isArray(boardsResponse.data)
        ? boardsResponse.data
        : (boardsResponse.data.pinouts || []);

      const icData = Array.isArray(icResponse.data)
        ? icResponse.data
        : (icResponse.data.data || []);

      setBoards(boardsData);
      setIcPinouts(icData);
      setTotalBoards(boardsData.length + icData.length);

      setBoardsCache(prev => new Map(prev).set('all-items', {
        boards: boardsData,
        icPinouts: icData,
      }));
      setLastFetchTime(now);
      setError(null);
    } catch (requestError) {
      console.error('Failed to fetch boards:', requestError);
      setError('Failed to load boards. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [boardsCache, lastFetchTime]);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  const searchableItems = useMemo(() => ([
    ...boards.map((board) => ({
      ...board,
      itemType: 'board',
      displayName: board.name || board.title || board.chip_name || 'Unnamed Board',
      displayBadge: board.package_type || 'Board',
      displayDescription: board.description || '',
      detailUrl: `/board/${board.slug || board.id}`,
      cardSvgSrc: buildBoardSvgPreviewSrc(board),
    })),
    ...icPinouts.map((ic) => ({
      ...ic,
      itemType: 'ic',
      displayName: ic.name || 'Unnamed IC',
      displayBadge: ic.package_variant || `${ic.package_type || 'IC'}-${ic.pin_count || ''}`,
      displayDescription: ic.description || '',
      detailUrl: `/ic-pinouts/${ic.slug}`,
    })),
  ]), [boards, icPinouts]);

  const matchesItemFilter = useCallback((item, filterKey) => {
    if (filterKey === 'all') {
      return true;
    }

    if (filterKey === 'ic') {
      return item.itemType === 'ic';
    }

    if (filterKey === 'board') {
      return item.itemType === 'board';
    }

    const haystack = [
      item.displayName,
      item.title,
      item.chip_name,
      item.name,
      item.description,
      item.manufacturer,
      item.category,
      item.package_type,
      item.package_variant,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    switch (filterKey) {
      case 'microcontroller':
        return item.itemType === 'board' && (
          haystack.includes('arduino')
          || haystack.includes('esp32')
          || haystack.includes('feather')
          || haystack.includes('pico')
        );
      case 'sensor':
        return haystack.includes('sensor');
      case 'logic-ic':
        return item.itemType === 'ic' && haystack.includes('logic');
      case 'timer-ic':
        return item.itemType === 'ic' && haystack.includes('timer');
      case 'op-amp':
        return item.itemType === 'ic' && (
          haystack.includes('op-amp')
          || haystack.includes('op amp')
          || haystack.includes('comparator')
        );
      case 'driver-ic':
        return item.itemType === 'ic' && haystack.includes('driver');
      case 'adafruit':
        return haystack.includes('adafruit');
      case 'espressif':
        return haystack.includes('espressif') || haystack.includes('esp');
      default:
        return true;
    }
  }, []);

  useEffect(() => {
    let filtered = searchableItems;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();

      filtered = filtered.filter((item) => {
        const haystack = [
          item.displayName,
          item.title,
          item.chip_name,
          item.name,
          item.description,
          item.manufacturer,
          item.category,
          item.package_type,
          item.package_variant,
          item.supported_protocols?.join?.(' '),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(term);
      });
    }

    if (activeFilter !== 'all') {
      filtered = filtered.filter((item) => matchesItemFilter(item, activeFilter));
    }

    const totalFiltered = filtered.length;
    const nextTotalPages = Math.ceil(totalFiltered / pageSize) || 1;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    setFilteredItems(paginatedItems);
    setTotalPages(nextTotalPages);
    setTotalBoards(totalFiltered);
  }, [searchableItems, searchTerm, activeFilter, currentPage, pageSize, matchesItemFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter]);

  const filterOptions = useMemo(() => ([
    { key: 'all', label: 'All' },
    { key: 'board', label: 'Boards' },
    { key: 'ic', label: 'ICs' },
    { key: 'microcontroller', label: 'Microcontrollers' },
    { key: 'sensor', label: 'Sensors' },
    { key: 'logic-ic', label: 'Logic ICs' },
    { key: 'timer-ic', label: 'Timer ICs' },
    { key: 'op-amp', label: 'Op-Amps' },
    { key: 'driver-ic', label: 'Driver ICs' },
    { key: 'adafruit', label: 'Adafruit' },
    { key: 'espressif', label: 'Espressif' },
  ]), []);

  const filterCounts = useMemo(() => (
    filterOptions.reduce((counts, option) => {
      counts[option.key] = option.key === 'all'
        ? searchableItems.length
        : searchableItems.filter((item) => matchesItemFilter(item, option.key)).length;

      return counts;
    }, {})
  ), [filterOptions, searchableItems, matchesItemFilter]);

  const popularBoards = useMemo(() => {
    return searchableItems
      .filter((item) => item.itemType === 'board' && Number(item.popular_on_home))
      .slice(0, POPULAR_BOARDS_LIMIT);
  }, [searchableItems]);

  const isQuickFilterActive = useCallback((chip) => {
    if (chip.filterKey) {
      return activeFilter === chip.filterKey && searchTerm === '';
    }

    return activeFilter === 'all' && searchTerm.toLowerCase() === chip.searchTerm;
  }, [activeFilter, searchTerm]);

  const applyQuickFilter = (chip) => {
    if (isQuickFilterActive(chip)) {
      setSearchTerm('');
      setActiveFilter('all');
      return;
    }

    setSearchTerm(chip.searchTerm || '');
    setActiveFilter(chip.filterKey || 'all');
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleBoardSelection = (boardId) => {
    const nextSelection = new Set(selectedBoards);

    if (nextSelection.has(boardId)) {
      nextSelection.delete(boardId);
    } else if (nextSelection.size < 2) {
      nextSelection.add(boardId);
    } else {
      alert('You can only compare up to 2 boards at a time. Please deselect a board first.');
      return;
    }

    setSelectedBoards(nextSelection);
  };

  const clearSelection = () => {
    setSelectedBoards(new Set());
  };

  const handleCompare = () => {
    if (selectedBoards.size >= 2) {
      const selectedBoardRecords = resolveCompareBoardSelection(Array.from(selectedBoards), boards);
      navigate(buildComparePath(selectedBoardRecords));
      clearSelection();
    }
  };

  const showingStart = totalBoards === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
  const showingEnd = Math.min(currentPage * pageSize, totalBoards);
  const isDefaultBrowse = !searchTerm && activeFilter === 'all';
  const showPopularBoards = isDefaultBrowse && popularBoards.length > 0;
  const resultsLabel = `${totalBoards} ${totalBoards === 1 ? 'result' : 'results'}`
    + (totalPages > 1 ? ` | Page ${currentPage} of ${totalPages}` : '');
  const mainSectionTitle = isDefaultBrowse ? 'All Pinouts' : 'Matching Pinouts';
  const mainSectionDescription = isDefaultBrowse
    ? 'Browse the full public library of boards, modules, sensors, and IC references.'
    : 'Refine the catalog with search and filters, then open any pinout for details.';

  if (loading) {
    return (
      <HomeContainer>
        <div className="container">
          <LoadingSpinner>
            <div className="loading-spinner"></div>
          </LoadingSpinner>
        </div>
      </HomeContainer>
    );
  }

  if (error) {
    return (
      <HomeContainer>
        <div className="container">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      </HomeContainer>
    );
  }

  return (
    <HomeContainer>
      <div className="container">
        <HeroSection>
          <HeroContent>
            <HeroTitle>Interactive Pinout Database</HeroTitle>
            <HeroSubtitle>
              Search detailed pinouts, GPIO capabilities, voltage limits, and board specs for
              microcontroller boards, modules, sensors, and ICs.
            </HeroSubtitle>

            <SearchSection>
              <SearchField>
                <SearchIcon size={20} />
                <SearchInput
                  type="search"
                  placeholder="Search boards, modules, sensors, ICs, manufacturers, or protocols..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  aria-label="Search boards and IC pinouts"
                  autoComplete="off"
                />
              </SearchField>

              <HeroMetaRow>
                <ResultsCount aria-live="polite">{resultsLabel}</ResultsCount>
              </HeroMetaRow>

              <QuickFilters>
                {QUICK_FILTERS.map((chip) => (
                  <QuickFilterChip
                    key={chip.key}
                    type="button"
                    $active={isQuickFilterActive(chip)}
                    onClick={() => applyQuickFilter(chip)}
                    aria-pressed={isQuickFilterActive(chip)}
                  >
                    {chip.label}
                  </QuickFilterChip>
                ))}
              </QuickFilters>
            </SearchSection>
          </HeroContent>
        </HeroSection>

        <BenefitsStrip aria-label="Why use MicroPinouts">
          {BENEFIT_CARDS.map((benefit) => {
            const Icon = benefit.icon;

            return (
              <BenefitCard key={benefit.title}>
                <BenefitIconWrap aria-hidden="true">
                  <Icon size={20} />
                </BenefitIconWrap>
                <div>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </div>
              </BenefitCard>
            );
          })}
        </BenefitsStrip>

        <BoardsViewLayout>
          <BoardsMain>
            {showPopularBoards && (
              <SectionBlock>
                <SectionHeader>
                  <SectionTitleGroup>
                    <SectionTitle>Popular Pinouts</SectionTitle>
                    <SectionDescription>
                      Start with a few of the most commonly searched boards in the database.
                    </SectionDescription>
                  </SectionTitleGroup>
                  <SectionMeta>{popularBoards.length} featured boards</SectionMeta>
                </SectionHeader>

                <FeaturedBoardsGrid>
                  {popularBoards.map((item, index) => (
                    <CatalogCard
                      key={`popular-${item.itemType}-${item.id}`}
                      item={item}
                      index={index}
                      compact
                    />
                  ))}
                </FeaturedBoardsGrid>
              </SectionBlock>
            )}

            <SectionBlock>
              <SectionHeader>
                <SectionTitleGroup>
                  <SectionTitle>{mainSectionTitle}</SectionTitle>
                  <SectionDescription>{mainSectionDescription}</SectionDescription>
                </SectionTitleGroup>
                <SectionMeta>
                  {totalBoards === 0 ? 'No matches' : `Showing ${showingStart}-${showingEnd} of ${totalBoards}`}
                </SectionMeta>
              </SectionHeader>

              {filteredItems.length > 0 ? (
                <BoardsGrid>
                  {filteredItems.map((item, index) => (
                    <CatalogCard
                      key={`${item.itemType}-${item.id}-${item.package_variant_id || ''}`}
                      item={item}
                      index={index}
                      showSelection={item.itemType === 'board'}
                      isSelected={selectedBoards.has(item.id)}
                      onToggleSelection={toggleBoardSelection}
                    />
                  ))}
                </BoardsGrid>
              ) : (
                <EmptyState>
                  <EmptyStateTitle>No results found</EmptyStateTitle>
                  <EmptyStateText>
                    {searchTerm || activeFilter !== 'all'
                      ? 'Try adjusting your search term or filter to find what you are looking for.'
                      : 'No boards are available at the moment.'}
                  </EmptyStateText>
                </EmptyState>
              )}

              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;

                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <PaginationButton
                        key={pageNum}
                        type="button"
                        $active={currentPage === pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                      >
                        {pageNum}
                      </PaginationButton>
                    );
                  })}

                  <PaginationButton
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PaginationButton>

                  <PaginationInfo>
                    Showing {showingStart}-{showingEnd} of {totalBoards}
                  </PaginationInfo>
                </PaginationContainer>
              )}
            </SectionBlock>
          </BoardsMain>

          <FiltersPanel>
            <FiltersHeader>
              <FiltersIconWrap aria-hidden="true">
                <SlidersHorizontal size={18} />
              </FiltersIconWrap>
              <div>
                <FiltersTitle>Filter by Family</FiltersTitle>
                <FiltersText>
                  Narrow the catalog by board type, IC family, sensor class, or vendor.
                </FiltersText>
              </div>
            </FiltersHeader>

            <FilterSection>
              {filterOptions.map((option) => (
                <FilterButton
                  key={option.key}
                  type="button"
                  $active={activeFilter === option.key}
                  onClick={() => setActiveFilter(option.key)}
                  aria-pressed={activeFilter === option.key}
                >
                  <FilterButtonLabel>{option.label}</FilterButtonLabel>
                  <FilterButtonCount $active={activeFilter === option.key}>
                    {filterCounts[option.key] ?? 0}
                  </FilterButtonCount>
                </FilterButton>
              ))}
            </FilterSection>
          </FiltersPanel>
        </BoardsViewLayout>

        <Copyright>
          <strong>&copy; 2026 micropinouts.com</strong>
          <br />
          All rights reserved.
          <br />
          Educational use and community sharing encouraged under applicable open-source licenses.
        </Copyright>
      </div>

      {selectedBoards.size > 0 && (
        <ComparisonBar>
          <ComparisonCount>
            {selectedBoards.size} of 2 boards selected for comparison
          </ComparisonCount>
          <CompareButton
            type="button"
            onClick={handleCompare}
            disabled={selectedBoards.size < 2}
          >
            <GitCompare size={16} />
            Compare ({selectedBoards.size}/2)
          </CompareButton>
          <ClearSelectionButton type="button" onClick={clearSelection}>
            Clear
          </ClearSelectionButton>
        </ComparisonBar>
      )}
    </HomeContainer>
  );
};

export default Home;
